/**
 * UAT Feedback Templates - Test Case Templates
 * 
 * Each template corresponds to a specific test case in the UAT suite.
 * Templates define which questions should be asked for each test case.
 * 
 * NOTE: Uses lowercase enum values for backend compatibility.
 */

import { TemplateCreate, TemplateQuestionCreate, FeedbackContext, StakeholderRole } from '@/services/api/questionBankService'

// Backend uses lowercase enum values
type BackendFeedbackContext = 'matrix' | 'upward_360' | 'employee_requested' | 'peer' | 'project'
type BackendStakeholderRole = 'campus_gm' | 'project_manager' | 'faculty_member' | 'department_head' | 'peer' | 'direct_report' | 'external' | 'student'

// Helper type that accepts backend lowercase values  
interface UATTemplateCreate extends Omit<TemplateCreate, 'feedback_context' | 'primary_stakeholder_role'> {
  feedback_context: BackendFeedbackContext
  primary_stakeholder_role?: BackendStakeholderRole
}

// ==================== Template Question Mappings ====================

// Common questions used in most templates
const COMMON_STEP_QUESTIONS: TemplateQuestionCreate[] = [
  { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 1 },
  { question_id: 'UAT-STEP-BLOCKER', is_required: false, display_order: 2 },
]

const COMMON_ISSUE_QUESTIONS: TemplateQuestionCreate[] = [
  { question_id: 'UAT-ISSUE-DESCRIPTION', is_required: false, display_order: 10 },
  { question_id: 'UAT-ISSUE-SEVERITY', is_required: false, display_order: 11 },
  { question_id: 'UAT-ERROR-MESSAGE', is_required: false, display_order: 12 },
]

const COMMON_USABILITY_QUESTIONS: TemplateQuestionCreate[] = [
  { question_id: 'UAT-EASE-OF-USE', is_required: false, display_order: 20 },
  { question_id: 'UAT-INSTRUCTIONS-CLEAR', is_required: false, display_order: 21 },
  { question_id: 'UAT-SUGGESTIONS', is_required: false, display_order: 22 },
]

const COMMON_SUMMARY_QUESTIONS: TemplateQuestionCreate[] = [
  { question_id: 'UAT-OVERALL-PASS', is_required: true, display_order: 100 },
  { question_id: 'UAT-TIME-TAKEN', is_required: false, display_order: 101 },
  { question_id: 'UAT-ADDITIONAL-NOTES', is_required: false, display_order: 102 },
]

// ==================== Authentication Test Templates ====================

const UAT_AUTH_TEMPLATES_RAW: UATTemplateCreate[] = [
  {
    template_code: 'UAT-TC001-AZURE-AD-LOGIN',
    template_name: 'TC-001: Azure AD Single Sign-On Login',
    description: 'Capture feedback for the Azure AD SSO login process. Tests successful authentication via Microsoft login and correct redirection to the platform.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 5,
    allow_custom_question: true,
    is_default: false,
    questions: [
      // Authentication-specific questions
      { question_id: 'UAT-AUTH-SSO-WORKED', is_required: true, display_order: 1 },
      { question_id: 'UAT-AUTH-REDIRECT-CORRECT', is_required: true, display_order: 2 },
      { question_id: 'UAT-AUTH-PROFILE-LOADED', is_required: true, display_order: 3 },
      // Standard issue reporting
      ...COMMON_ISSUE_QUESTIONS,
      // Usability
      { question_id: 'UAT-EASE-OF-USE', is_required: false, display_order: 20 },
      // Summary
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC002-FIRST-TIME-USER',
    template_name: 'TC-002: First-Time User Login Flow',
    description: 'Capture feedback for first-time user detection and redirection to onboarding. Tests that new users are correctly identified and sent to the onboarding wizard.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 5,
    allow_custom_question: true,
    is_default: false,
    questions: [
      ...COMMON_STEP_QUESTIONS,
      { question_id: 'UAT-AUTH-SSO-WORKED', is_required: true, display_order: 3 },
      { question_id: 'UAT-AUTH-REDIRECT-CORRECT', is_required: true, display_order: 4,
        custom_question_text: 'Were you redirected to the ONBOARDING page (not Team Member Journey)?'
      },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC003-RETURNING-USER',
    template_name: 'TC-003: Returning User Login',
    description: 'Capture feedback for returning user authentication. Tests that users who have completed onboarding are correctly redirected to the Team Member Journey.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 5,
    allow_custom_question: true,
    is_default: false,
    questions: [
      ...COMMON_STEP_QUESTIONS,
      { question_id: 'UAT-AUTH-SSO-WORKED', is_required: true, display_order: 3 },
      { question_id: 'UAT-AUTH-REDIRECT-CORRECT', is_required: true, display_order: 4,
        custom_question_text: 'Were you redirected to the TEAM MEMBER JOURNEY page (not Onboarding)?'
      },
      { question_id: 'UAT-AUTH-PROFILE-LOADED', is_required: true, display_order: 5 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC004-PERSONA-SWITCHING',
    template_name: 'TC-004: Test Persona Switching',
    description: 'Capture feedback for the persona switching feature used during UAT. Tests ability to switch between Real Profile, Team Member Persona, and People Leader Persona.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 10,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 1,
        custom_question_text: 'Were you able to access the persona switcher from the profile menu?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 2,
        custom_question_text: 'Could you successfully switch to the Team Member Persona?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 3,
        custom_question_text: 'Could you successfully switch to the People Leader Persona?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 4,
        custom_question_text: 'Could you switch back to your Real Profile?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 5,
        custom_question_text: 'Did the sidebar menu change appropriately for each persona?'
      },
      ...COMMON_ISSUE_QUESTIONS,
      { question_id: 'UAT-CONFUSION-POINTS', is_required: false, display_order: 22 },
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
]

// ==================== Onboarding Test Templates ====================

const UAT_ONBOARDING_TEMPLATES_RAW: UATTemplateCreate[] = [
  {
    template_code: 'UAT-TC010-ONBOARDING-WIZARD',
    template_name: 'TC-010: Onboarding Wizard Complete Flow',
    description: 'Comprehensive feedback for the complete onboarding wizard flow. Tests all steps from welcome through to completion including the new profile completion step.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 15,
    allow_custom_question: true,
    is_default: false,
    questions: [
      // Step 1: Welcome
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 1,
        custom_question_text: 'Step 1 (Welcome): Did the welcome screen display correctly with your name?'
      },
      // Step 2: F&O Data Confirmation
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 2,
        custom_question_text: 'Step 2 (F&O Confirmation): Could you view and confirm your team member data?'
      },
      { question_id: 'UAT-OB-FO-DATA-ACCURATE', is_required: true, display_order: 3 },
      // Step 3: Profile Completion (NEW)
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 4,
        custom_question_text: 'Step 3 (Profile Completion): Could you add qualifications and career history?'
      },
      { question_id: 'UAT-OB-PROFILE-SAVE', is_required: true, display_order: 5 },
      // Step 4: Objective Planning (or People Leader Orientation first if people leader)
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 6,
        custom_question_text: 'Final Step: Could you complete objective planning or request a meeting?'
      },
      // Interactive demo (after onboarding)
      { question_id: 'UAT-OB-INTERACTIVE-TOUR', is_required: false, display_order: 7 },
      // Overall flow
      { question_id: 'UAT-OB-STEP-FLOW', is_required: true, display_order: 8 },
      { question_id: 'UAT-OB-PROGRESS-VISIBLE', is_required: true, display_order: 9 },
      // Issues & Usability
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC011-FO-DATA-CONFIRMATION',
    template_name: 'TC-011: F&O Data Confirmation Step',
    description: 'Focused feedback on the F&O data confirmation step. Tests that team member data is correctly synced and displayed, with options to confirm or report issues.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 5,
    allow_custom_question: true,
    is_default: false,
    questions: [
      // Data display verification
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 1,
        custom_question_text: 'Was your Full Name displayed correctly?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 2,
        custom_question_text: 'Was your Job Title displayed correctly?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 3,
        custom_question_text: 'Was your Function displayed correctly?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 4,
        custom_question_text: 'Was your Campus/Location displayed correctly?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 5,
        custom_question_text: 'Was your People Leader name displayed correctly?'
      },
      // Data accuracy
      { question_id: 'UAT-OB-FO-DATA-ACCURATE', is_required: true, display_order: 6 },
      // Confirmation flow
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 7,
        custom_question_text: 'Were both "Confirm" and "Report Issue" options available?'
      },
      // Issues
      ...COMMON_ISSUE_QUESTIONS,
      { question_id: 'UAT-PARTIAL-SUCCESS', is_required: false, display_order: 15,
        custom_question_text: 'If any data was wrong, which fields were incorrect?'
      },
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC014-MANAGER-ORIENTATION',
    template_name: 'TC-014: People Leader Setup Step',
    description: 'Feedback for the people leader-specific team confirmation step. Tests that people leaders can verify their direct reports and understand dual platform access. Feature training is provided via interactive tour on the People Leader Hub page.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 5,
    allow_custom_question: true,
    is_default: false,
    questions: [
      // People leader detection
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 1,
        custom_question_text: 'Were you correctly identified as a people leader and shown the People Leader Setup step?'
      },
      // Team verification
      { question_id: 'UAT-MGR-TEAM-CORRECT', is_required: true, display_order: 2 },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 3,
        custom_question_text: 'Was your direct reports list displayed with names, titles, and emails?'
      },
      // Confirmation flow
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 4,
        custom_question_text: 'Could you use "Confirm Team is Correct" button?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 5,
        custom_question_text: 'Was the "Report Incorrect" option available?'
      },
      // After confirmation
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 6,
        custom_question_text: 'After confirming, did you see the dual platform access explanation (Team Member + People Leader Journey)?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 7,
        custom_question_text: 'Was the interactive tour notice shown for the People Leader Journey?'
      },
      { question_id: 'UAT-STEP-COMPLETE', is_required: true, display_order: 8,
        custom_question_text: 'Was your people leader name displayed correctly?'
      },
      // People Leader Journey Tour (post-onboarding)
      { question_id: 'UAT-OB-INTERACTIVE-TOUR', is_required: false, display_order: 9,
        custom_question_text: 'Did the interactive tour trigger when visiting the People Leader Journey page?'
      },
      // Usability
      { question_id: 'UAT-MGR-CONTENT-HELPFUL', is_required: false, display_order: 20 },
      { question_id: 'UAT-CONFUSION-POINTS', is_required: false, display_order: 21 },
      // Issues
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
]

// ==================== Team Member Journey Templates ====================

const UAT_EMPLOYEE_JOURNEY_TEMPLATES_RAW: UATTemplateCreate[] = [
  {
    template_code: 'UAT-TC020-OBJECTIVE-SETTING',
    template_name: 'TC-020: Objective Setting and Configuration',
    description: 'Capture feedback for the objective setting workflow including the 5-step wizard, strategic cascade alignment, KPI configuration, and SMART analysis.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 20,
    allow_custom_question: true,
    is_default: false,
    questions: [
      // Wizard flow
      { question_id: 'UAT-OBJ-WIZARD-STEPS', is_required: true, display_order: 1 },
      { question_id: 'UAT-OBJ-KPA-SELECT', is_required: true, display_order: 2 },
      { question_id: 'UAT-OBJ-STRATEGIC-CASCADE', is_required: true, display_order: 3 },
      { question_id: 'UAT-OBJ-AI-SUGGESTIONS', is_required: false, display_order: 4 },
      { question_id: 'UAT-OBJ-KPI-ADD', is_required: true, display_order: 5 },
      { question_id: 'UAT-OBJ-SMART-ANALYSIS', is_required: true, display_order: 6 },
      { question_id: 'UAT-OBJ-SUBMIT-STATUS', is_required: true, display_order: 7 },
      // Standard questions
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC021-VIEW-OBJECTIVES',
    template_name: 'TC-021: View Objectives and KPA Workspace',
    description: 'Capture feedback for viewing assigned objectives within the KPA workspace, including navigation, KPI tracking, and strategic context.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 10,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-VIEW-KPA-CARDS', is_required: true, display_order: 1 },
      { question_id: 'UAT-VIEW-KPA-WORKSPACE', is_required: true, display_order: 2 },
      { question_id: 'UAT-VIEW-KPI-VALUES', is_required: true, display_order: 3 },
      { question_id: 'UAT-VIEW-YEAR-SELECTOR', is_required: false, display_order: 4 },
      { question_id: 'UAT-VIEW-STRATEGIC-CONTEXT', is_required: false, display_order: 5 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC022-ADD-EVIDENCE',
    template_name: 'TC-022: Add and Manage Evidence',
    description: 'Capture feedback for evidence collection including adding KPI measurements, file uploads, period filtering, and evidence management.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 15,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-EV-ADD-FROM-KPI', is_required: true, display_order: 1 },
      { question_id: 'UAT-EV-MEASUREMENT', is_required: true, display_order: 2 },
      { question_id: 'UAT-EV-FILE-UPLOAD', is_required: true, display_order: 3 },
      { question_id: 'UAT-EV-PROGRESS-UPDATE', is_required: true, display_order: 4 },
      { question_id: 'UAT-EV-PERIOD-FILTER', is_required: false, display_order: 5 },
      { question_id: 'UAT-EV-EDIT-DELETE', is_required: false, display_order: 6 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC023-PERFORMANCE-REVIEW',
    template_name: 'TC-023: 1:1 Check-In Dialogue Preparation and Participation',
    description: 'Capture feedback for team member review preparation including self-reflection, AI assistance (Tier 1/2), and KPA-by-KPA discussion.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 20,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-REV-PERIOD-CARDS', is_required: true, display_order: 1 },
      { question_id: 'UAT-REV-PREPARE-BUTTON', is_required: true, display_order: 2 },
      { question_id: 'UAT-REV-AI-TIER1', is_required: false, display_order: 3 },
      { question_id: 'UAT-REV-AI-TIER2', is_required: false, display_order: 4 },
      { question_id: 'UAT-REV-KPA-TABS', is_required: true, display_order: 5 },
      { question_id: 'UAT-REV-DISCUSSION-FIELDS', is_required: true, display_order: 6 },
      { question_id: 'UAT-REV-SAVE-DRAFT', is_required: false, display_order: 7 },
      { question_id: 'UAT-REV-TIER34-HIDDEN', is_required: true, display_order: 8 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC025-IDP-MANAGEMENT',
    template_name: 'TC-025: My Personalised Growth (IDP) Management',
    description: 'Capture feedback for Individual Development Plan management including career objectives, strengths, growth opportunities, and interventions.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 15,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-IDP-TABS-NAV', is_required: true, display_order: 1 },
      { question_id: 'UAT-IDP-CAREER-OBJ', is_required: true, display_order: 2 },
      { question_id: 'UAT-IDP-ADD-ITEMS', is_required: true, display_order: 3 },
      { question_id: 'UAT-IDP-KPA-LINK', is_required: false, display_order: 4 },
      { question_id: 'UAT-IDP-PENDING-STATUS', is_required: true, display_order: 5 },
      { question_id: 'UAT-IDP-MGR-FEEDBACK', is_required: false, display_order: 6 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
]

// ==================== People Leader Journey Templates ====================

const UAT_MANAGER_JOURNEY_TEMPLATES_RAW: UATTemplateCreate[] = [
  {
    template_code: 'UAT-TC030-TEAM-OVERVIEW',
    template_name: 'TC-030: Team Overview and Member Management',
    description: 'Capture feedback for the People Leader Journey team overview including team member visibility, statistics, alerts, and member details dialog.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 10,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-TEAM-MEMBERS-VISIBLE', is_required: true, display_order: 1 },
      { question_id: 'UAT-TEAM-SUMMARY-STATS', is_required: true, display_order: 2 },
      { question_id: 'UAT-TEAM-MEMBER-CARD', is_required: true, display_order: 3 },
      { question_id: 'UAT-TEAM-PENDING-ALERTS', is_required: false, display_order: 4 },
      { question_id: 'UAT-TEAM-FILTERS', is_required: false, display_order: 5 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC031-OBJECTIVE-CONFIG',
    template_name: 'TC-031: Team Objective Configuration',
    description: 'Capture feedback for people leader objective configuration including reviewing pending objectives, collaborative sessions, approval/rejection, and strategic cascade view.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 20,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-OBJ-VIEW-PENDING', is_required: true, display_order: 1 },
      { question_id: 'UAT-OBJ-COLLAB-SESSION', is_required: true, display_order: 2 },
      { question_id: 'UAT-OBJ-APPROVE', is_required: true, display_order: 3 },
      { question_id: 'UAT-OBJ-REJECT', is_required: false, display_order: 4 },
      { question_id: 'UAT-OBJ-ALLOCATE', is_required: false, display_order: 5 },
      { question_id: 'UAT-OBJ-STRATEGIC-VIEW', is_required: false, display_order: 6 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC032-CONDUCT-REVIEW',
    template_name: 'TC-032: Conduct 1:1 Check-In Dialogue',
    description: 'Capture feedback for conducting 1:1 check-in dialogues including celebration-first process, 4-tier AI assistance, KPA indicators, and dialogue completion.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 25,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-CREV-INITIATE', is_required: true, display_order: 1 },
      { question_id: 'UAT-CREV-PERIOD-FILTER', is_required: true, display_order: 2 },
      { question_id: 'UAT-CREV-AI-TIER1', is_required: false, display_order: 3 },
      { question_id: 'UAT-CREV-AI-TIER2', is_required: false, display_order: 4 },
      { question_id: 'UAT-CREV-AI-TIER3', is_required: false, display_order: 5 },
      { question_id: 'UAT-CREV-AI-TIER4', is_required: false, display_order: 6 },
      { question_id: 'UAT-CREV-KPA-RATING', is_required: true, display_order: 7 },
      { question_id: 'UAT-CREV-DISCUSSION-STEPS', is_required: true, display_order: 8 },
      { question_id: 'UAT-CREV-ADD-EVIDENCE', is_required: false, display_order: 9 },
      { question_id: 'UAT-CREV-COMPLETE', is_required: true, display_order: 10 },
      { question_id: 'UAT-CREV-INFORMAL', is_required: false, display_order: 11 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
  {
    template_code: 'UAT-TC033-TEAM-DEVELOPMENT',
    template_name: 'TC-033: Team Development (My Personalised Growth) Management',
    description: 'Capture feedback for managing team My Personalised Growths including reviewing, approving, rejecting, and editing career objectives, strengths, opportunities, and interventions.',
    feedback_context: 'project',
    primary_stakeholder_role: 'external',
    estimated_completion_time: 15,
    allow_custom_question: true,
    is_default: false,
    questions: [
      { question_id: 'UAT-TDEV-VIEW-PLANS', is_required: true, display_order: 1 },
      { question_id: 'UAT-TDEV-PENDING-ITEMS', is_required: true, display_order: 2 },
      { question_id: 'UAT-TDEV-APPROVE-ITEMS', is_required: true, display_order: 3 },
      { question_id: 'UAT-TDEV-REJECT-REASON', is_required: false, display_order: 4 },
      { question_id: 'UAT-TDEV-EDIT-ITEMS', is_required: false, display_order: 5 },
      { question_id: 'UAT-TDEV-ADD-ITEMS', is_required: false, display_order: 6 },
      { question_id: 'UAT-TDEV-MANAGER-NOTES', is_required: false, display_order: 7 },
      ...COMMON_ISSUE_QUESTIONS,
      ...COMMON_USABILITY_QUESTIONS,
      ...COMMON_SUMMARY_QUESTIONS,
    ],
  },
]

// ==================== Cast and Export Templates ====================

export const UAT_AUTH_TEMPLATES = UAT_AUTH_TEMPLATES_RAW as unknown as TemplateCreate[]
export const UAT_ONBOARDING_TEMPLATES = UAT_ONBOARDING_TEMPLATES_RAW as unknown as TemplateCreate[]
export const UAT_EMPLOYEE_JOURNEY_TEMPLATES = UAT_EMPLOYEE_JOURNEY_TEMPLATES_RAW as unknown as TemplateCreate[]
export const UAT_MANAGER_JOURNEY_TEMPLATES = UAT_MANAGER_JOURNEY_TEMPLATES_RAW as unknown as TemplateCreate[]

export const ALL_UAT_TEMPLATES: TemplateCreate[] = [
  ...UAT_AUTH_TEMPLATES,
  ...UAT_ONBOARDING_TEMPLATES,
  ...UAT_EMPLOYEE_JOURNEY_TEMPLATES,
  ...UAT_MANAGER_JOURNEY_TEMPLATES,
]

// ==================== Template Lookup by Test Case ID ====================

export const getTemplateByTestCase = (testCaseId: string): TemplateCreate | undefined => {
  const mapping: Record<string, string> = {
    // Authentication
    'TC-001': 'UAT-TC001-AZURE-AD-LOGIN',
    'TC-002': 'UAT-TC002-FIRST-TIME-USER',
    'TC-003': 'UAT-TC003-RETURNING-USER',
    'TC-004': 'UAT-TC004-PERSONA-SWITCHING',
    // Onboarding
    'TC-010': 'UAT-TC010-ONBOARDING-WIZARD',
    'TC-011': 'UAT-TC011-FO-DATA-CONFIRMATION',
    'TC-014': 'UAT-TC014-MANAGER-ORIENTATION',
    // Employee Journey
    'TC-020': 'UAT-TC020-OBJECTIVE-SETTING',
    'TC-021': 'UAT-TC021-VIEW-OBJECTIVES',
    'TC-022': 'UAT-TC022-ADD-EVIDENCE',
    'TC-023': 'UAT-TC023-PERFORMANCE-REVIEW',
    'TC-025': 'UAT-TC025-IDP-MANAGEMENT',
    // Manager Journey
    'TC-030': 'UAT-TC030-TEAM-OVERVIEW',
    'TC-031': 'UAT-TC031-OBJECTIVE-CONFIG',
    'TC-032': 'UAT-TC032-CONDUCT-REVIEW',
    'TC-033': 'UAT-TC033-TEAM-DEVELOPMENT',
  }
  
  const templateCode = mapping[testCaseId]
  if (!templateCode) return undefined
  
  return ALL_UAT_TEMPLATES.find(t => t.template_code === templateCode)
}
